import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SportsSoccer as GoalIcon,
  GroupAdd as TeamIcon,
  EmojiEvents as CaptainIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { get, post, patch } from "../utils/networkUtils";

const boosterConfig = {
  goal_bonus: {
    icon: GoalIcon,
    color: "#4caf50",
  },
  "12th_man": {
    icon: TeamIcon,
    color: "#2196f3",
  },
  max_captain: {
    icon: CaptainIcon,
    color: "#ff9800",
  },
};

const BoosterConfigurationPage = () => {
  const [boosters, setBoosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedBooster, setExpandedBooster] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch boosters on mount
  const fetchBoosters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("[BoosterConfig] Fetching boosters...");
      const response = await get("/admin/boosters");
      console.log("[BoosterConfig] Response:", response);

      if (response?.data?.boosters && response.data.boosters.length > 0) {
        const populated = response.data.boosters.map((b) => {
          const base =
            b.code === "goal_bonus"
              ? {
                  normal_player_goal_points: 10,
                  captain_goal_points: 20,
                  vice_captain_goal_points: 20,
                }
              : b.code === "12th_man"
              ? { can_be_captain: false, max_players: 12 }
              : b.code === "max_captain"
              ? { multiplier: 2, disables_normal_captain: true }
              : {};
          return {
            ...b,
            metadata: { ...base, ...(b.metadata || {}) },
          };
        });
        console.log("[BoosterConfig] Setting boosters:", populated);
        setBoosters(populated);
      } else {
        console.log("[BoosterConfig] No boosters found, auto-creating...");
        // Auto-create boosters if none exist
        await createDefaultBoosters();
      }
    } catch (err) {
      console.error("[BoosterConfig] Failed to fetch boosters:", err);
      setError("Failed to load boosters. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoosters();
  }, [fetchBoosters]);

  const createDefaultBoosters = async () => {
    try {
      const response = await post("/admin/boosters/auto-create", {});

      if (response?.data?.boosters) {
        const populated = response.data.boosters.map((b) => {
          const base =
            b.code === "goal_bonus"
              ? {
                  normal_player_goal_points: 10,
                  captain_goal_points: 20,
                  vice_captain_goal_points: 20,
                }
              : b.code === "12th_man"
              ? { can_be_captain: false, max_players: 12 }
              : b.code === "max_captain"
              ? { multiplier: 2, disables_normal_captain: true }
              : {};
          return {
            ...b,
            metadata: { ...base, ...(b.metadata || {}) },
          };
        });
        setBoosters(populated);
      }
    } catch (err) {
      console.error("Failed to auto-create boosters:", err);
      setError("Failed to initialize boosters. Please refresh the page.");
    }
  };

  const handleToggleBooster = (index) => {
    const newBoosters = [...boosters];
    newBoosters[index].is_enabled = !newBoosters[index].is_enabled;
    setBoosters(newBoosters);
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleMaxUsesChange = (index, value) => {
    const newBoosters = [...boosters];
    // Allow empty string during typing; validate on blur
    if (value === "") {
      newBoosters[index].max_uses_per_user = "";
    } else {
      const parsed = parseInt(value, 10);
      newBoosters[index].max_uses_per_user = isNaN(parsed) ? "" : parsed;
    }
    setBoosters(newBoosters);
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleMaxUsesBlur = (index) => {
    const newBoosters = [...boosters];
    const val = newBoosters[index].max_uses_per_user;
    let parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed < 1) parsed = 1;
    if (parsed > 10) parsed = 10;
    newBoosters[index].max_uses_per_user = parsed;
    setBoosters(newBoosters);
  };

  const handleMetadataChange = (index, field, value) => {
    const newBoosters = [...boosters];
    // Allow empty string during typing for number fields
    if (value === "") {
      newBoosters[index].metadata = {
        ...newBoosters[index].metadata,
        [field]: "",
      };
    } else {
      const parsed = parseInt(value, 10);
      newBoosters[index].metadata = {
        ...newBoosters[index].metadata,
        [field]: isNaN(parsed) ? "" : parsed,
      };
    }
    setBoosters(newBoosters);
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleMetadataBlur = (index, field, defaultValue = 1) => {
    const newBoosters = [...boosters];
    const val = newBoosters[index].metadata[field];
    let parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed < 1) parsed = defaultValue;
    newBoosters[index].metadata = {
      ...newBoosters[index].metadata,
      [field]: parsed,
    };
    setBoosters(newBoosters);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Save each booster that has changes
      for (const booster of boosters) {
        const parsedMaxUses = parseInt(booster.max_uses_per_user, 10);
        const maxUses =
          isNaN(parsedMaxUses) || parsedMaxUses < 1
            ? 1
            : Math.min(10, parsedMaxUses);

        // Sanitize metadata number fields
        const sanitizedMetadata = { ...booster.metadata };
        const numberFields = [
          "normal_player_goal_points",
          "captain_goal_points",
          "vice_captain_goal_points",
          "multiplier",
          "max_players",
        ];
        for (const key of numberFields) {
          if (key in sanitizedMetadata) {
            const parsed = parseInt(sanitizedMetadata[key], 10);
            sanitizedMetadata[key] = isNaN(parsed) || parsed < 1 ? 1 : parsed;
          }
        }

        await patch(`/admin/boosters/${booster.id}`, {
          is_enabled: booster.is_enabled,
          max_uses_per_user: maxUses,
          metadata: sanitizedMetadata,
        });
      }

      setSuccessMessage("Booster configuration saved successfully");
      setHasChanges(false);
    } catch (err) {
      console.error("Failed to save boosters:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (code) => {
    setExpandedBooster(expandedBooster === code ? null : code);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Booster Configuration | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Booster Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure boosters for the tournament. Enable/disable boosters, set
            usage limits, and customize behavior.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {boosters.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No boosters found. The system will automatically create default
            boosters when you navigate to this page. If you see this message,
            please check the browser console for errors.
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {boosters.map((booster, index) => {
            const config = boosterConfig[booster.code];
            const Icon = config.icon;
            const isExpanded = expandedBooster === booster.code;

            return (
              <Card key={booster.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: config.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "white", fontSize: 32 }} />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h5" component="div">
                          {booster.name}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={booster.is_enabled ? "Enabled" : "Disabled"}
                            color={booster.is_enabled ? "success" : "default"}
                            size="small"
                          />
                          <Switch
                            checked={booster.is_enabled}
                            onChange={() => handleToggleBooster(index)}
                          />
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {booster.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <TextField
                          label="Max Uses per User"
                          type="number"
                          value={booster.max_uses_per_user}
                          onChange={(e) =>
                            handleMaxUsesChange(index, e.target.value)
                          }
                          onBlur={() => handleMaxUsesBlur(index)}
                          inputProps={{ min: 1, max: 10 }}
                          size="small"
                          sx={{ width: 150 }}
                        />
                        <Tooltip title="Configure detailed settings">
                          <IconButton
                            onClick={() => toggleExpand(booster.code)}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Collapse in={isExpanded && booster.is_enabled}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Advanced Configuration
                        </Typography>

                        {booster.code === "goal_bonus" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Normal Player Goal Points"
                              type="number"
                              value={
                                booster.metadata?.normal_player_goal_points ??
                                ""
                              }
                              onChange={(e) =>
                                handleMetadataChange(
                                  index,
                                  "normal_player_goal_points",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                handleMetadataBlur(
                                  index,
                                  "normal_player_goal_points",
                                  10
                                )
                              }
                              inputProps={{ min: 1 }}
                              size="small"
                              helperText="Points per goal for normal players (default: 10)"
                            />
                            <TextField
                              label="Captain Goal Points"
                              type="number"
                              value={
                                booster.metadata?.captain_goal_points ?? ""
                              }
                              onChange={(e) =>
                                handleMetadataChange(
                                  index,
                                  "captain_goal_points",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                handleMetadataBlur(
                                  index,
                                  "captain_goal_points",
                                  20
                                )
                              }
                              inputProps={{ min: 1 }}
                              size="small"
                              helperText="Points per goal for Captain (default: 20)"
                            />
                            <TextField
                              label="Vice-Captain Goal Points"
                              type="number"
                              value={
                                booster.metadata?.vice_captain_goal_points ?? ""
                              }
                              onChange={(e) =>
                                handleMetadataChange(
                                  index,
                                  "vice_captain_goal_points",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                handleMetadataBlur(
                                  index,
                                  "vice_captain_goal_points",
                                  20
                                )
                              }
                              inputProps={{ min: 1 }}
                              size="small"
                              helperText="Points per goal for Vice-Captain (default: 20)"
                            />
                          </Box>
                        )}

                        {booster.code === "12th_man" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Max Players"
                              type="number"
                              value={booster.metadata?.max_players ?? 12}
                              disabled
                              size="small"
                              helperText="Total players when 12th Man is active (fixed at 12)"
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Switch
                                checked={
                                  booster.metadata?.can_be_captain || false
                                }
                                disabled
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                12th Man can be Captain (always disabled)
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {booster.code === "max_captain" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Multiplier"
                              type="number"
                              value={booster.metadata?.multiplier ?? ""}
                              onChange={(e) =>
                                handleMetadataChange(
                                  index,
                                  "multiplier",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                handleMetadataBlur(index, "multiplier", 2)
                              }
                              inputProps={{ min: 1 }}
                              size="small"
                              helperText="Points multiplier for Max Captain (default: 2x)"
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Switch
                                checked={
                                  booster.metadata?.disables_normal_captain ??
                                  true
                                }
                                disabled
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Disables normal Captain/Vice-Captain (always
                                enabled)
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, mb: 4 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            size="large"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default BoosterConfigurationPage;
